import {
  ComponentProps,
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from "react";
import { ImageSourcePropType } from "react-native";
import Markdown from "react-native-markdown-display";
import { ArrowLeft, ArrowRight } from "@tamagui/lucide-icons";
import {
  styled,
  Text,
  Image as TGImage,
  useMedia,
  View,
  XStack,
} from "tamagui";

import { Button } from "../../ui";

export interface Tutorial {
  title: string;
  desc: string;
  buttonNext: string;
  content: {
    image?: (string & (string | ImageSourcePropType)) | undefined;
  };
}

const TutorialContext = createContext<{
  data: Tutorial[] | undefined;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
}>({
  data: undefined,
  step: 0,
  setStep: () => console.warn("Should be used in TutorialContextProvider"),
  onClose: () => console.warn("Should be used in TutorialContextProvider"),
});

const TutorialContextProvider: React.FC<
  PropsWithChildren & {
    data: Tutorial[] | undefined;
    onClose: () => void;
  }
> = ({ children, data, onClose }) => {
  const [step, setStep] = useState(0);

  return (
    <TutorialContext.Provider
      value={{
        data: data,
        step,
        setStep,
        onClose,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("Should be used in TutorialContextProvider");
  }

  const dataLenght = context.data?.length ?? 0;

  const next = () =>
    context.setStep((prev) => {
      if (prev === dataLenght - 1) {
        context.onClose();
        return prev;
      }
      return prev + 1;
    });

  const prev = () =>
    context.setStep((prev) => {
      if (prev === 0) return prev;
      return prev - 1;
    });

  const currentData = context.data?.[context.step];

  return {
    prev,
    next,
    currentData,
    step: context.step,
    steps: dataLenght,
    isPrevDisabled: context.step === 0,
  };
};

const GAP_BETWEEN_ITEMS = 2;

type ProgressBarProps = React.ComponentProps<typeof XStack>;

export const TutorialProgressBar = ({ ...rest }: ProgressBarProps) => {
  const [itemWidth, setItemWidth] = useState(0);
  const { steps, step } = useTutorial();
  const count = [...Array(steps).keys()];
  const totalGapBetweenItems = GAP_BETWEEN_ITEMS * steps;

  return (
    <XStack
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setItemWidth((width - totalGapBetweenItems) / steps);
      }}
      alignItems="center"
      gap={GAP_BETWEEN_ITEMS}
      width="100%"
      {...rest}
    >
      {count.map((item, index) => (
        <ProgressItem
          isFirstItem={index === 0}
          isLastItem={index === count.length - 1}
          itemWidth={itemWidth}
          key={item}
          full={item <= step}
        />
      ))}
    </XStack>
  );
};

const ProgressItem = ({
  full,
  itemWidth,
  isFirstItem,
  isLastItem,
}: {
  full: boolean;
  itemWidth: number;
  isFirstItem: boolean;
  isLastItem: boolean;
}) => {
  return (
    <XStack
      height={4}
      width={`${itemWidth}px`}
      backgroundColor="$gray-teal-100"
      style={{
        ...(isFirstItem && {
          borderTopLeftRadius: 4,
          borderBottomLeftRadius: 4,
        }),
        ...(isLastItem && {
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
        }),
      }}
      alignItems="center"
      justifyContent="center"
    >
      <View
        height="100%"
        width={full ? "100%" : "0%"}
        backgroundColor="$green-500"
        style={{
          ...(isFirstItem && {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          }),
          ...(isLastItem && {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
          }),
        }}
      />
    </XStack>
  );
};

const TutotialTitleBase = styled(Text, {
  name: "TutotialTitle",
  fontFamily: "$subtitle",
  color: "$gray-primary-900",
  variants: {
    font: {
      lg: {
        fontWeight: "$7",
        fontSize: "$8",
        lineHeight: "$8",
      },
      md: {
        fontWeight: "$6",
        fontSize: "$6",
        lineHeight: "$6",
        textAlign: "center",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type TutotialTitleProps = ComponentProps<typeof TutotialTitleBase>;

const TutotialTitle: React.FC<TutotialTitleProps> = (props) => {
  const { currentData } = useTutorial();
  const { sm } = useMedia();
  return (
    <TutotialTitleBase
      data-id={"tutorial-title"}
      font={sm ? "md" : "lg"}
      {...props}
    >
      {currentData?.title}
    </TutotialTitleBase>
  );
};

type TutorialDescriptionProps = ComponentProps<typeof Markdown>;

const TutorialDescription: React.FC<TutorialDescriptionProps> = ({
  ...rest
}) => {
  const { currentData } = useTutorial();
  const { sm } = useMedia();

  return (
    <View data-id={"tutorial-description"}>
      <Markdown
        style={{
          body: {
            fontWeight: 500,
            color: "#23252A",
            fontFamily: "Fixel Text",
            ...(!sm && {
              fontSize: 20,
              lineHeight: 30,
            }),
            ...(sm && {
              fontSize: 16,
              lineHeight: 24,
            }),
          },
        }}
        {...rest}
      >
        {currentData?.desc}
      </Markdown>
    </View>
  );
};

const TutorialControls = () => {
  const { next, prev, isPrevDisabled, currentData } = useTutorial();
  const { sm } = useMedia();

  return (
    <XStack
      $sm={{
        width: "100%",
        justifyContent: "center",
      }}
      gap={"$1"}
    >
      <Button
        data-id={"tutorial-prev-btn"}
        disabled={isPrevDisabled}
        onPress={prev}
        mode="icon"
        variant="gray"
        size="lg"
        rounded="lg"
        {...(sm && {
          size: "md",
        })}
      >
        <ArrowLeft color={"$gray-primary-900"} size={"$2.5"} />
      </Button>

      <Button
        data-id={"tutorial-next-btn"}
        variant="primary"
        size="lg"
        rounded="lg"
        iconAfter={<ArrowRight color={"white"} size={"$2.5"} />}
        flex={1}
        maxWidth={308}
        onPress={next}
        {...(sm && {
          size: "md",
        })}
      >
        {currentData?.buttonNext}
      </Button>
    </XStack>
  );
};

const Image = () => {
  const { currentData } = useTutorial();
  return (
    <TGImage
      resizeMode={"contain"}
      width={"100%"}
      maxWidth={570}
      maxHeight={570}
      src={currentData?.content.image}
      $sm={{
        maxWidth: 240,
        maxHeight: 240,
      }}
    />
  );
};

export const Tutorial = {
  Provider: TutorialContextProvider,
  Controls: TutorialControls,
  Description: TutorialDescription,
  Title: TutotialTitle,
  Progress: TutorialProgressBar,
  Image,
};
